import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { Send, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { NicolAIAvatar } from '@/shared/components'
import { useAIAssistant } from '../hooks/use-ai-assistant'
import { SuggestionCard } from './suggestion-card'

interface ChatPanelProps {
  onInjectField: (field: string, content: string) => void
  variant?: 'desktop' | 'mobile'
}

export function ChatPanel({ onInjectField, variant = 'desktop' }: ChatPanelProps) {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const {
    messages,
    isLoading,
    error,
    currentSuggestions,
    isRateLimited,
    sendMessage,
    clearSuggestion,
    retry,
  } = useAIAssistant()

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, currentSuggestions])

  // Focus textarea on mount (desktop only)
  useEffect(() => {
    if (variant === 'desktop') {
      textareaRef.current?.focus()
    }
  }, [variant])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (input.trim() && !isLoading && !isRateLimited) {
      sendMessage(input)
      setInput('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleUseSuggestion = (index: number) => {
    const suggestion = currentSuggestions[index]
    if (suggestion) {
      onInjectField(suggestion.field, suggestion.content)
      clearSuggestion(index)
    }
  }

  const inputHeight = variant === 'desktop' ? 'h-[120px]' : 'h-20'
  const padding = variant === 'desktop' ? 'p-8' : 'p-4'
  const inputPadding = variant === 'desktop' ? 'p-8' : 'p-4'

  return (
    <div className="flex h-full flex-col">
      {/* Messages */}
      <div className={cn('flex-1 overflow-y-auto bg-background-secondary', padding)}>
        <div className="flex flex-col gap-6">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                'flex gap-3',
                message.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              {message.role === 'assistant' && (
                <NicolAIAvatar size="lg" />
              )}
              <div
                className={cn(
                  'max-w-[80%] px-4 py-3',
                  message.role === 'assistant'
                    ? 'rounded-[0_16px_16px_16px] border border-border bg-white'
                    : 'rounded-[16px_16px_0_16px] bg-primary text-white'
                )}
              >
                {message.role === 'assistant' && (
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-sm font-semibold text-accent">NicolAI</span>
                    <span className="text-xs text-foreground-subtle">
                      {new Date(message.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                )}
                {message.role === 'assistant' ? (
                  <div className="prose prose-sm max-w-none text-foreground prose-p:my-2 prose-p:leading-relaxed prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-strong:text-foreground prose-a:text-accent">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-white">
                    {message.content}
                  </p>
                )}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-foreground-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('precadrage.assistant.thinking')}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-center justify-between rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
              <button
                type="button"
                onClick={retry}
                className="flex items-center gap-1 text-xs font-medium hover:underline"
              >
                <RefreshCw className="h-3 w-3" />
                {t('common.retry')}
              </button>
            </div>
          )}

          {/* Current suggestions */}
          {currentSuggestions.map((suggestion, index) => (
            <SuggestionCard
              key={`${suggestion.field}-${index}`}
              suggestion={suggestion}
              onUse={() => handleUseSuggestion(index)}
            />
          ))}

          {/* Rate limit warning */}
          {isRateLimited && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <AlertCircle className="h-4 w-4" />
              {t('precadrage.assistant.rateLimited')}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div
        className={cn(
          'shrink-0 border-t border-border bg-background',
          inputPadding
        )}
      >
        <form onSubmit={handleSubmit} className="flex items-end gap-3">
          <div className="relative flex-1">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('precadrage.assistant.placeholder')}
              disabled={isLoading || isRateLimited}
              className={cn(
                'w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground',
                'placeholder:text-foreground-muted',
                'focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent',
                'disabled:cursor-not-allowed disabled:opacity-50',
                inputHeight
              )}
            />
          </div>
          <button
            type="submit"
            disabled={!input.trim() || isLoading || isRateLimited}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send className="h-[18px] w-[18px]" />
          </button>
        </form>
      </div>
    </div>
  )
}
